import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { notifications } from '@mantine/notifications';
import { useEventStream } from '@commander/api-client';
const EventStreamContext = createContext({
    isConnected: false,
    subscribe: () => () => { },
});
export function useEventStreamStatus() {
    return useContext(EventStreamContext);
}
/** Subscribe to SSE events from a child component. Cleans up on unmount. */
export function useCommanderEvents(listener) {
    const { subscribe } = useContext(EventStreamContext);
    const listenerRef = useRef(listener);
    listenerRef.current = listener;
    useEffect(() => {
        const unsubscribe = subscribe((event) => listenerRef.current(event));
        return unsubscribe;
    }, [subscribe]);
}
/** Human-readable labels for result codes. */
const RESULT_LABELS = {
    Acknowledged: 'Acknowledged by radio',
    NegativeAcknowledged: 'Rejected by radio',
    NoReply: 'Radio did not respond',
    Error: 'Gateway error',
};
function resultLabel(resultCode) {
    if (!resultCode)
        return '';
    return RESULT_LABELS[resultCode] ?? resultCode;
}
/** Notification config for command completion events. */
const COMMAND_NOTIFICATION = {
    'stun.completed': { name: 'Stun', successMsg: 'has been stunned', failureMsg: 'stun failed' },
    'unstun.completed': { name: 'Unstun', successMsg: 'has been unstunned', failureMsg: 'unstun failed' },
    'radio_check.completed': { name: 'Radio Check', successMsg: 'is reachable', failureMsg: 'is unreachable' },
};
export function EventStreamProvider({ children, enabled = true, }) {
    const [isConnected, setIsConnected] = useState(false);
    const listenersRef = useRef(new Set());
    const subscribe = useCallback((listener) => {
        listenersRef.current.add(listener);
        return () => { listenersRef.current.delete(listener); };
    }, []);
    const handleEvent = useCallback((event) => {
        // Forward to all subscribers
        for (const listener of listenersRef.current) {
            listener(event);
        }
        // Show notifications for important events
        switch (event.type) {
            case 'stun.completed':
            case 'unstun.completed':
            case 'radio_check.completed': {
                const cfg = COMMAND_NOTIFICATION[event.type];
                const detail = resultLabel(event.resultCode);
                const suffix = detail ? ` — ${detail}` : '';
                notifications.show({
                    title: event.success ? `${cfg.name} Successful` : `${cfg.name} Failed`,
                    message: `Radio ${event.radioId} ${event.success ? cfg.successMsg : cfg.failureMsg}${suffix}`,
                    color: event.success ? 'green' : 'red',
                });
                break;
            }
            case 'session.created':
                notifications.show({
                    title: 'New Session',
                    message: 'Equipment checked out',
                    color: 'blue',
                });
                break;
            case 'session.ended':
                notifications.show({
                    title: 'Session Ended',
                    message: 'Equipment returned',
                    color: 'teal',
                });
                break;
        }
    }, []);
    const handleConnect = useCallback(() => {
        setIsConnected(true);
        console.log('[SSE] Connected to event stream');
    }, []);
    const handleDisconnect = useCallback(() => {
        setIsConnected(false);
        console.log('[SSE] Disconnected from event stream, reconnecting...');
    }, []);
    useEventStream({
        enabled,
        onEvent: handleEvent,
        onConnect: handleConnect,
        onDisconnect: handleDisconnect,
    });
    return (_jsx(EventStreamContext.Provider, { value: { isConnected, subscribe }, children: children }));
}
//# sourceMappingURL=EventStreamProvider.js.map